import { computed, ref } from "vue";
import { useListNavContext, saveListNavContext } from "./useListNavContext";
import { logger } from "@/infrastructure/services/LoggerService";
/** The list's own request for `page`: the same view, inline AST and quick filters as the list that saved it. */
function pageParams(sq, page) {
    return {
        filterId: sq.query.filterId ?? undefined,
        ast: sq.query.ast,
        refinement: sq.query.refinement,
        search: sq.query.search,
        page,
        pageSize: sq.query.pageSize,
        sortBy: sq.query.sortBy,
        sortOrder: sq.query.sortOrder,
    };
}
export function useListNavPager(scope, currentId, fetchFn) {
    const { previousId, nextId, hasPreviousPage, hasNextPage, storedQuery, refresh } = useListNavContext(scope, currentId);
    const isFetchingPrevious = ref(false);
    const isFetchingNext = ref(false);
    const isPreviousDisabled = computed(() => previousId.value === null && !hasPreviousPage.value);
    const isNextDisabled = computed(() => nextId.value === null && !hasNextPage.value);
    async function goToPrevious(navigate) {
        if (previousId.value) {
            await navigate(previousId.value);
            return;
        }
        const sq = storedQuery.value;
        if (!hasPreviousPage.value || !sq)
            return;
        isFetchingPrevious.value = true;
        try {
            const prevPage = sq.pagination.currentPage - 1;
            const response = await fetchFn(pageParams(sq, prevPage));
            const fetchedIds = response.result.map((item) => item.id);
            if (!fetchedIds.length)
                return;
            // Await navigate first — if the unsaved-changes guard cancels the navigation
            // (user clicks Stay), router.push() resolves with a NavigationFailure (truthy).
            // Only update the stored context once we know the navigation went through.
            const failure = await navigate(fetchedIds[fetchedIds.length - 1]);
            if (failure)
                return;
            saveListNavContext(scope, fetchedIds, {
                query: { ...sq.query, page: response.currentPage },
                pagination: { currentPage: response.currentPage, totalPages: response.totalPages },
            });
            refresh();
        }
        catch (e) {
            logger.error(`useListNavPager(${scope}) goToPrevious failed`, e);
        }
        finally {
            isFetchingPrevious.value = false;
        }
    }
    async function goToNext(navigate) {
        if (nextId.value) {
            await navigate(nextId.value);
            return;
        }
        const sq = storedQuery.value;
        if (!hasNextPage.value || !sq)
            return;
        isFetchingNext.value = true;
        try {
            const nextPage = sq.pagination.currentPage + 1;
            const response = await fetchFn(pageParams(sq, nextPage));
            const fetchedIds = response.result.map((item) => item.id);
            if (!fetchedIds.length)
                return;
            // Await navigate first — if the unsaved-changes guard cancels the navigation
            // (user clicks Stay), router.push() resolves with a NavigationFailure (truthy).
            // Only update the stored context once we know the navigation went through.
            const failure = await navigate(fetchedIds[0]);
            if (failure)
                return;
            saveListNavContext(scope, fetchedIds, {
                query: { ...sq.query, page: response.currentPage },
                pagination: { currentPage: response.currentPage, totalPages: response.totalPages },
            });
            refresh();
        }
        catch (e) {
            logger.error(`useListNavPager(${scope}) goToNext failed`, e);
        }
        finally {
            isFetchingNext.value = false;
        }
    }
    return {
        previousId,
        nextId,
        isPreviousDisabled,
        isNextDisabled,
        isFetchingPrevious,
        isFetchingNext,
        goToPrevious,
        goToNext,
    };
}
